import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { hasPermission } from "../lib/permissions";
import {
  AUTONOMY_LABELS, PILOT_STATUS_LABELS, REASON_SEVERITY_LABELS, SYNC_CHOICES, SYNC_LABELS,
  activityText, activationMissing, assigneeOptions, buildReportPayload, buildShareRows, capabilitiesOf, healthTone, isClosed, isRunning, openFollowups,
  performerOptions, pilotErrorMessage, pilotStatusClass, proposesFollowup, roleInPilotLabel, sameSelection, validatePlanning,
} from "../lib/pilots";
import usePilotReports from "../hooks/usePilotReports";
import PilotTeamCard, { MembersDialog } from "./PilotTeam";
import PilotTasksCard, { ReassignDialog } from "./PilotTeamTasks";
import PilotReportsCard, { ReportDialog } from "./PilotReports";
import PilotHealthBadge from "./PilotHealthBadge";
import { Row, SectionTitle, fmtDate, fmtInstant } from "./PilotBits";
import {
  ActivatePilotDialog, AutonomyDialog, CancelPilotDialog, CompletePilotDialog, CreatePilotDialog, FollowupDialog, ResponsibleSelect,
  SharingDialog,
} from "./PilotDialogs";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { ResponsiveDropdownMenu as DropdownMenu, ResponsiveDropdownMenuContent as DropdownMenuContent, ResponsiveDropdownMenuItem as DropdownMenuItem, ResponsiveDropdownMenuTrigger as DropdownMenuTrigger } from "./ui/responsive-dropdown-menu";
import { toast } from "sonner";
import DeleteWithReasonDialog from "./DeleteWithReasonDialog";
import { AlertTriangle, ArrowUpRight, CalendarClock, Info, Loader2, MoreHorizontal, Plus, RefreshCw, ShieldCheck } from "lucide-react";


export default function PilotPanel({ prospect, pilotState, user, directory, tasks, teamTasks, onChange, onReload, onTasksChanged, onOpenTask, onGoToTab }) {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const canManagePilot = hasPermission(user, "prospects:manage_pilot");
  const canCreateTasks = hasPermission(user, "tasks:create");
  const canChooseAssignees = hasPermission(user, "tasks:scope_all");

  // One in-flight mutation at a time: the confirm buttons are disabled while `busy`, and `run` itself refuses to start twice.
  const run = async (request, success, fallback, { keepOpen = false } = {}) => {
    if (busy) return false;
    setBusy(true);
    try {
      const { data } = await request();
      onChange(data);
      if (success) toast.success(success);
      if (!keepOpen) setDialog(null);  // the team dialog stays open: several changes in a row are the normal use
      return true;
    } catch (e) {
      toast.error(pilotErrorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Same discipline for the actions that do not answer with the pilot (reports, hand-overs): one at a time, the caller refreshes what it shows.
  const runAction = async (request, success, fallback) => {
    if (busy) return false;
    setBusy(true);
    try {
      await request();
      if (success) toast.success(success);
      setDialog(null);
      return true;
    } catch (e) {
      toast.error(pilotErrorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  if (pilotState.status === "loading") {
    return <div className="space-y-3" data-testid="pilot-loading"><Skeleton className="h-24 w-full" /><Skeleton className="h-40 w-full" /></div>;
  }

  if (pilotState.status === "error") {
    return (
      <Card className="p-6 text-center space-y-3" data-testid="pilot-error">
        <AlertTriangle className="mx-auto text-amber-500" size={20} />
        <p className="text-sm text-slate-700">{pilotState.error || "Impossible de charger le pilote."}</p>
        <Button variant="outline" onClick={onReload} data-testid="pilot-retry"><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
      </Card>
    );
  }

  if (pilotState.status === "none") {
    return (
      <>
        <Card className="p-8 text-center space-y-3" data-testid="pilot-empty">
          <ShieldCheck className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">Cette pharmacie n'est pas encore pilote</h3>
          {canManagePilot ? (
            <>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Un pilote suit l'avancement réel d'une pharmacie qui démarre avec Jamora : période, responsable, autonomie et santé calculée à partir de faits.</p>
              <Button  onClick={() => setDialog("create")} data-testid="pilot-create-button"><Plus size={14} className="mr-1.5" />Créer le pilote</Button>
            </>
          ) : <p className="text-sm text-slate-500" data-testid="pilot-no-rights">Vous n'avez pas le droit de créer un pilote.</p>}
        </Card>
        <CreatePilotDialog open={dialog === "create"} onClose={() => setDialog(null)} busy={busy} directory={directory} user={user} pharmacyName={prospect.pharmacy_name}
          onSubmit={(body) => run(() => api.post(`/prospects/${prospect.id}/pilot`, body), "Pilote créé", "Impossible de créer le pilote.")} />
      </>
    );
  }

  return (
    <PilotDetail
      pilot={pilotState.data} prospect={prospect} user={user} directory={directory} tasks={teamTasks ? teamTasks.items : tasks} teamTasks={teamTasks} busy={busy} run={run}
      runAction={runAction} dialog={dialog} setDialog={setDialog}
      canCreateTasks={canCreateTasks} canChooseAssignees={canChooseAssignees} onTasksChanged={onTasksChanged} onOpenTask={onOpenTask} onGoToTab={onGoToTab}
    />
  );
}

function PilotDetail({ pilot, prospect, user, directory, tasks, teamTasks, busy, run, runAction, dialog, setDialog, canCreateTasks, canChooseAssignees, onTasksChanged, onOpenTask, onGoToTab }) {
  const cap = capabilitiesOf(pilot);
  const limited = pilot.access === "limited";
  const computed = pilot.computed || {};
  const health = computed.health;
  const running = isRunning(pilot);
  const url = `/prospects/${prospect.id}/pilot`;
  const proposal = proposesFollowup(pilot);
  const [taskFilter, setTaskFilter] = useState("team");
  const [reassigning, setReassigning] = useState(null);
  const [editingReport, setEditingReport] = useState(null);
  const { reports, reloadReports, loadMoreReports } = usePilotReports(prospect.id);
  const options = assigneeOptions({ pilot, directory, canScopeAll: canChooseAssignees });
  const searchIntervenants = useCallback(async (q) => (await api.get(`${url}/intervenant-candidates`, { params: { q } })).data.items, [url]);
  const canPickIntervenants = !!user?.features?.collaborators && hasPermission(user, "collaborators:view");
  const defaultAssignee = canChooseAssignees && directory.some((u) => u.id === pilot.responsible_user_id) ? pilot.responsible_user_id : user?.id;

  const activate = ({ start, end }) => run(async () => {
    if (start !== (pilot.start_date || "") || end !== (pilot.end_date || "")) await api.patch(url, { start_date: start, end_date: end });
    return api.post(`${url}/activate`);
  }, "Pilote activé", "Impossible d'activer le pilote.");

  const createFollowup = async (body) => {
    if (busy) return;
    try {
      await api.post("/tasks", { ...body, domain: "pilots", task_type: "suivi_pilote", prospect_id: prospect.id });
      toast.success("Suivi planifié");
      setDialog(null);
      onTasksChanged();
    } catch (e) {
      toast.error(pilotErrorMessage(e, "Impossible de planifier le suivi."));
    }
  };

  return (
    <div className="space-y-4" data-testid="pilot-panel" data-pilot-status={pilot.status}>
      {limited && (
        <div className="flex items-start gap-2 rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-600" data-testid="pilot-limited-banner">
          <Info size={16} className="mt-0.5 shrink-0" />Vous suivez ce pilote en tant que {(roleInPilotLabel(pilot.role_in_pilot) || "membre de l'équipe").toLowerCase()}. La clôture et la planification sont décidées par l'équipe qui suit la pharmacie.
        </div>
      )}

      <Card className="relative p-4 md:p-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 pr-12 md:pr-0">
            <h3 className="font-semibold text-slate-900 mr-1">Pilote</h3>
            <span className={`text-2xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded ${pilotStatusClass(pilot.status)}`} data-testid="pilot-status-badge">{PILOT_STATUS_LABELS[pilot.status]}</span>
            {health && <PilotHealthBadge state={health.state} />}
          </div>
          <div className="flex items-center gap-2">
            {cap.manageLifecycle && pilot.status === "planned" && <Button  onClick={() => setDialog("activate")} data-testid="pilot-activate-button">Activer le pilote</Button>}
            {cap.manageLifecycle && pilot.status === "active" && <Button  onClick={() => setDialog("complete")} data-testid="pilot-complete-button">Terminer le pilote</Button>}
            {cap.manageLifecycle && running && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="outline" size="icon" className="absolute right-4 top-4 md:static shrink-0" aria-label="Autres actions du pilote" data-testid="pilot-more-button"><MoreHorizontal size={16} /></Button></DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setDialog("cancel")} data-testid="pilot-cancel-menu">Annuler le pilote…</DropdownMenuItem>
                  {pilot.status === "planned" && hasPermission(user, "prospects:delete_pilot") && (
                    <DropdownMenuItem onClick={() => setDialog("delete")} className="text-red-600 focus:text-red-700" data-testid="pilot-delete-menu">Supprimer ce pilote…</DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {!cap.manageLifecycle && running && !limited && <p className="text-xs text-slate-400 mt-2" data-testid="pilot-readonly-note">Lecture seule : vous n'avez pas le droit de gérer ce pilote.</p>}
      </Card>

      <DeleteWithReasonDialog
        target={dialog === "delete" ? pilot : null} testId="delete-pilot" onClose={() => setDialog(null)}
        title="Supprimer ce pilote prévu ?"
        description="Il n'a encore rien produit : il est effacé définitivement. Un pilote qui a démarré, un compte rendu ou une tâche l'en empêchent — il faut alors l'annuler."
        onConfirm={async (reason) => {
          try {
            await api.delete(`/prospects/${prospect.id}/pilot`, { data: { reason } });
            toast.success("Pilote supprimé");
            setDialog(null);
            onReload?.();
          } catch (e) {
            toast.error(pilotErrorMessage(e, "Impossible de supprimer ce pilote."));
            setDialog(null);
          }
        }}
      />

      <PilotClosure pilot={pilot} />

      {health && (
        <Card className="p-4 md:p-5" data-testid="pilot-health-card">
          <SectionTitle hint="Calculée à partir de faits, sans score">Santé du pilote</SectionTitle>
          {health.reasons.length === 0 && <p className="text-sm text-slate-600">Aucun point d'attention.</p>}
          <ul className="space-y-2">
            {health.reasons.map((r) => (
              <li key={r.code} className="flex gap-2.5 text-sm" data-testid={`pilot-reason-${r.code}`}>
                <span className={`mt-1.5 h-2 w-2 rounded-full shrink-0 ${healthTone(r.severity).dot}`} aria-hidden="true" />
                <div className="min-w-0">
                  <div className="font-medium text-slate-900">{r.label} <span className="text-2xs font-normal text-slate-400">· {REASON_SEVERITY_LABELS[r.severity]}</span></div>
                  <div className="text-slate-600 break-words">{r.detail}</div>
                  {!limited && r.refs.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-2">
                      {r.refs.filter((id) => tasks.some((t) => t.id === id)).map((id) => (
                        <button key={id} type="button" onClick={() => onOpenTask(id)} className="text-xs text-jamora hover:underline" data-testid={`pilot-reason-task-${id}`}>
                          Voir la tâche « {tasks.find((t) => t.id === id).title} »
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {health.evidence.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100" data-testid="pilot-evidence">
              <div className="text-2xs font-semibold uppercase tracking-wider text-slate-400 mb-1">Éléments constatés</div>
              <ul className="space-y-0.5 text-sm text-slate-600">{health.evidence.map((e) => <li key={e}>{e}</li>)}</ul>
            </div>
          )}
        </Card>
      )}

      <FollowupCard
        pilot={pilot} tasks={tasks} proposal={proposal} limited={limited} canCreateTasks={canCreateTasks && running}
        onPlan={() => setDialog("followup")} onOpenTask={onOpenTask} onGoToTab={onGoToTab}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <PilotTeamCard pilot={pilot} onManage={() => setDialog("members")} />
        <PilotReportsCard
          reports={reports} prospectId={prospect.id} canDelete={hasPermission(user, "prospects:delete_pilot")} canWrite={cap.manageOperational && running} onCreate={() => { setEditingReport(null); setDialog("report"); }}
          onEdit={(r) => { setEditingReport(r); setDialog("report"); }} onMore={loadMoreReports} onRetry={() => reloadReports()}
        />
      </div>

      {teamTasks && (
        <PilotTasksCard
          pilot={pilot} teamTasks={teamTasks} filter={taskFilter} onFilter={setTaskFilter} canCreate={canCreateTasks && running}
          onCreate={() => setDialog("followup")} onOpen={onOpenTask} onReassign={setReassigning} onRetry={onTasksChanged}
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlanningCard pilot={pilot} cap={cap} directory={directory} user={user} run={run} busy={busy} url={url} />
        <ProgressCard pilot={pilot} computed={computed} onGoToTab={onGoToTab} limited={limited} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SyncCard pilot={pilot} cap={cap} run={run} busy={busy} url={url} />
        <AutonomyCard pilot={pilot} cap={cap} directory={directory} user={user} onEvaluate={() => setDialog("autonomy")} />
      </div>

      {limited
        ? <ContactCard contact={pilot.contact} />
        : cap.manageSharing && <SharingCard pilot={pilot} prospect={prospect} run={run} busy={busy} url={url} dialog={dialog} setDialog={setDialog} />}

      <ActivatePilotDialog pilot={pilot} open={dialog === "activate"} onClose={() => setDialog(null)} busy={busy} onSubmit={activate} />
      <CompletePilotDialog open={dialog === "complete"} onClose={() => setDialog(null)} busy={busy}
        onSubmit={(note) => run(() => api.post(`${url}/complete`, { note }), "Pilote terminé", "Impossible de terminer le pilote.")} />
      <CancelPilotDialog open={dialog === "cancel"} onClose={() => setDialog(null)} busy={busy}
        onSubmit={(reason) => run(() => api.post(`${url}/cancel`, { reason }), "Pilote annulé", "Impossible d'annuler le pilote.")} />
      <AutonomyDialog pilot={pilot} open={dialog === "autonomy"} onClose={() => setDialog(null)} busy={busy}
        onSubmit={(body) => run(() => api.post(`${url}/autonomy`, body), "Autonomie évaluée", "Impossible d'enregistrer l'évaluation.")} />
      <FollowupDialog open={dialog === "followup"} onClose={() => setDialog(null)} busy={busy} pharmacyName={prospect.pharmacy_name}
        options={options} defaultAssignee={defaultAssignee} user={user} onSubmit={createFollowup} />
      <MembersDialog
        open={dialog === "members"} onClose={() => setDialog(null)} pilot={pilot} directory={directory} teamTasks={teamTasks ? teamTasks.items : []} busy={busy}
        onAdd={(ids) => run(() => api.post(`${url}/members`, { user_ids: ids }), "Intervenant ajouté", "Impossible d'ajouter l'intervenant.", { keepOpen: true })}
        onRemove={async (id, reassignTo) => {
          const ok = await run(() => api.delete(`${url}/members/${id}`, { params: reassignTo ? { reassign_to: reassignTo } : {} }), "Intervenant retiré", "Impossible de retirer l'intervenant.", { keepOpen: true });
          if (ok) onTasksChanged();
          return ok;
        }}
        onChangePrincipal={(id, keep) => run(() => api.patch(url, { responsible_user_id: id, keep_previous_as_member: keep }), "Principal modifié", "Impossible de modifier le principal.", { keepOpen: true })}
        onSearchIntervenants={canPickIntervenants ? searchIntervenants : undefined}
        onAddIntervenant={(id) => run(() => api.post(`${url}/members`, { collaborator_ids: [id] }), "Intervenant sans compte ajouté", "Impossible d'ajouter l'intervenant.", { keepOpen: true })}
        onRemoveIntervenant={(id) => run(() => api.delete(`${url}/intervenants/${id}`), "Intervenant retiré", "Impossible de retirer l'intervenant.", { keepOpen: true })}
      />
      <ReportDialog
        open={dialog === "report"} report={editingReport} busy={busy} onClose={() => setDialog(null)} performers={performerOptions(pilot)}
        onSubmit={async (form) => {
          const body = buildReportPayload(form, editingReport);
          if (editingReport && Object.keys(body).length === 0) { setDialog(null); return; }
          const ok = await runAction(() => (editingReport ? api.patch(`${url}/reports/${editingReport.id}`, body) : api.post(`${url}/reports`, body)),
            editingReport ? "Compte rendu modifié" : "Compte rendu publié", "Impossible d'enregistrer le compte rendu.");
          if (ok) reloadReports({ silent: true });
        }}
      />
      <ReassignDialog
        open={!!reassigning} task={reassigning} pilot={pilot} busy={busy} onClose={() => setReassigning(null)}
        onSubmit={async (task, target) => {
          const ok = await runAction(() => api.patch(`/tasks/${task.id}`, { assigned_to: [target] }), "Tâche réattribuée", "Impossible de réattribuer la tâche.");
          if (ok) onTasksChanged();
          return ok;
        }}
      />
    </div>
  );
}

function PilotClosure({ pilot }) {
  if (!isClosed(pilot) || !pilot.closure) return null;
  const c = pilot.closure;
  const snap = c.snapshot;
  return (
    <Card className="p-4 md:p-5 border-slate-300 bg-slate-50" data-testid="pilot-closure">
      <SectionTitle>Clôture</SectionTitle>
      <p className="text-sm text-slate-900 mb-2">
        Pilote {pilot.status === "completed" ? "terminé" : "annulé"} le {fmtDate((c.closed_at || "").slice(0, 10))}. Il est conservé en lecture seule.
      </p>
      {c.reason && <Row label="Motif">{c.reason}</Row>}
      {c.note && <Row label="Note">{c.note}</Row>}
      {snap && (
        <div className="mt-2 pt-2 border-t border-slate-200" data-testid="pilot-closure-snapshot">
          <div className="text-2xs font-semibold uppercase tracking-wider text-slate-400 mb-1">État figé à la clôture</div>
          {snap.health && <Row label="Santé"><PilotHealthBadge state={snap.health.state} testid="pilot-closure-health" /></Row>}
          <Row label="Autonomie">{AUTONOMY_LABELS[snap.autonomy?.level] || "Non évaluée"}</Row>
          {snap.onboarding && <Row label="Onboarding">{snap.onboarding.modules_done} / {snap.onboarding.modules_total} modules</Row>}
          <Row label="Tâches">{snap.open_tasks} ouverte(s), dont {snap.overdue_tasks} en retard</Row>
        </div>
      )}
    </Card>
  );
}

function FollowupCard({ pilot, tasks, proposal, limited, canCreateTasks, onPlan, onOpenTask, onGoToTab }) {
  const next = pilot.computed?.next_followup;
  const followups = useMemo(() => openFollowups(tasks), [tasks]);
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-followup-card">
      <SectionTitle>Prochain suivi</SectionTitle>
      {next ? (
        <div className="flex items-center justify-between gap-3" data-testid="pilot-next-followup">
          <div className="text-sm text-slate-900 min-w-0"><CalendarClock size={14} className="inline mr-1.5 text-slate-400" />{next.title} <span className="text-slate-500">· {fmtDate(next.due_date)}</span></div>
          {tasks.some((t) => t.id === next.task_id) && <button type="button" className="text-xs text-jamora hover:underline shrink-0" onClick={() => onOpenTask(next.task_id)}>Ouvrir</button>}
        </div>
      ) : (
        <div className={`rounded-md p-3 text-sm ${proposal ? "bg-amber-50 text-amber-900" : "bg-slate-50 text-slate-600"}`} data-testid="pilot-no-followup">
          Aucun suivi planifié.{proposal && " Planifier un suivi permet de ne pas perdre le fil de ce pilote."}
        </div>
      )}
      {followups.length > 1 && (
        <ul className="mt-3 space-y-1 text-sm text-slate-600" data-testid="pilot-followup-list">
          {followups.map((t) => (
            <li key={t.id} className="flex justify-between gap-3"><button type="button" className="text-left hover:underline truncate" onClick={() => onOpenTask(t.id)}>{t.title}</button><span className="shrink-0 text-slate-500">{fmtDate(t.due_date)}</span></li>
          ))}
        </ul>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {canCreateTasks && (
          <Button variant={proposal ? "default" : "outline"} size="sm" onClick={onPlan} data-testid="pilot-plan-followup">
            <Plus size={14} className="mr-1" />Planifier un suivi
          </Button>
        )}
        {!limited && onGoToTab && <button type="button" className="text-xs text-jamora hover:underline inline-flex items-center gap-1" onClick={() => onGoToTab("tasks")}>Toutes les tâches <ArrowUpRight size={12} /></button>}
      </div>
    </Card>
  );
}

function PlanningCard({ pilot, cap, directory, user, run, busy, url }) {
  const [form, setForm] = useState({ start: "", end: "", responsible: "" });
  const [keepPrevious, setKeepPrevious] = useState(true);
  const stamp = pilot.updated_at;
  useEffect(() => { setForm({ start: pilot.start_date || "", end: pilot.end_date || "", responsible: pilot.responsible_user_id }); }, [stamp]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = form.start !== (pilot.start_date || "") || form.end !== (pilot.end_date || "") || form.responsible !== pilot.responsible_user_id;
  const rangeError = validatePlanning(form);
  const activeNeedsDates = pilot.status === "active" && activationMissing(form).length > 0;
  const save = () => run(() => api.patch(url, {
    ...(form.start !== (pilot.start_date || "") && { start_date: form.start || null }),
    ...(form.end !== (pilot.end_date || "") && { end_date: form.end || null }),
    ...(form.responsible !== pilot.responsible_user_id && { responsible_user_id: form.responsible, keep_previous_as_member: keepPrevious }),
  }), "Modifications enregistrées", "Impossible d'enregistrer les modifications.");
  const editable = cap.managePlanning;
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-planning-card">
      <SectionTitle>Période et principal</SectionTitle>
      {editable ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label htmlFor="plan-start">Début</Label><Input id="plan-start" type="date" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} data-testid="plan-start" /></div>
            <div className="space-y-1.5"><Label htmlFor="plan-end">Fin</Label><Input id="plan-end" type="date" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} data-testid="plan-end" /></div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="plan-responsible">Principal du pilote</Label>
            {directory.length > 0 ? <ResponsibleSelect id="plan-responsible" value={form.responsible} onChange={(v) => setForm({ ...form, responsible: v })} directory={directory} selfId={user?.id} />
              : <p className="text-sm text-slate-900">{pilot.responsible_name || "Aucun responsable"}</p>}
          </div>
          {form.responsible !== pilot.responsible_user_id && (
            <label className="flex items-center gap-2 text-xs text-slate-600" data-testid="plan-keep-previous-row">
              <Checkbox checked={keepPrevious} onCheckedChange={(v) => setKeepPrevious(v === true)} data-testid="plan-keep-previous" />
              Garder l'ancien principal comme intervenant
            </label>
          )}
          {(rangeError || activeNeedsDates) && <p role="alert" className="text-xs text-red-600" data-testid="plan-error">{rangeError || "Un pilote actif conserve ses dates de début et de fin."}</p>}
          {dirty && <div className="flex justify-end"><Button  size="sm" disabled={busy || !!rangeError || activeNeedsDates} onClick={save} data-testid="plan-save">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button></div>}
        </div>
      ) : (
        <>
          <Row label="Début">{fmtDate(pilot.start_date)}</Row>
          <Row label="Fin">{fmtDate(pilot.end_date)}</Row>
          <Row label="Principal">{pilot.responsible_name || "Aucun responsable"}</Row>
        </>
      )}
    </Card>
  );
}

function ProgressCard({ pilot, computed, onGoToTab, limited }) {
  const o = computed.onboarding;
  const m = computed.migration;
  const act = computed.last_operational_activity;
  const t = computed.tasks;
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-progress-card">
      <SectionTitle hint="Lu dans l'onboarding et la mise en place">Avancement</SectionTitle>
      <div className="space-y-3">
        <div data-testid="pilot-onboarding-summary">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Onboarding</span>
            {o ? <span className="text-slate-900">{o.modules_done} / {o.modules_total} modules</span>
              : <span className="text-slate-900">{computed.onboarding_state === "no_history" ? "Historique non enregistré" : "Non démarré"}</span>}</div>
          {o && <Progress value={o.modules_total ? Math.round((o.modules_done / o.modules_total) * 100) : 0} className="h-1.5 mt-1.5" />}
          {o && o.phases.length > 0 && <ul className="mt-1.5 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-0.5">{o.phases.map((ph) => <li key={ph.id}>{ph.name} {ph.done}/{ph.total}</li>)}</ul>}
          {!limited && onGoToTab && <button type="button" className="mt-1 text-xs text-jamora hover:underline" onClick={() => onGoToTab("onboarding")}>Voir l'onglet Onboarding</button>}
        </div>
        <div data-testid="pilot-migration-summary">
          <div className="flex justify-between text-sm"><span className="text-slate-500">Mise en place des données</span>
            <span className="text-slate-900">{m ? `${m.steps_done} / 4 étapes` : "Non démarrée"}</span></div>
          {!limited && onGoToTab && <button type="button" className="mt-1 text-xs text-jamora hover:underline" onClick={() => onGoToTab("migration")}>Voir l'onglet Mise en place</button>}
        </div>
        <div className="border-t border-slate-100 pt-2">
          <Row label="Dernière activité terrain" testid="pilot-last-activity">
            {act ? <>{activityText(act)} · {fmtInstant(act.at)}</> : "Aucune enregistrée"}
          </Row>
          <Row label="Tâches" testid="pilot-task-counters">{t.open_count} ouverte(s) · {t.overdue_count} en retard{t.critical_overdue_count ? ` (${t.critical_overdue_count} critique(s))` : ""}</Row>
        </div>
      </div>
    </Card>
  );
}

function SyncCard({ pilot, cap, run, busy, url }) {
  const [form, setForm] = useState({ sync: "none", last: "", notes: "" });
  const stamp = pilot.updated_at;
  useEffect(() => { setForm({ sync: pilot.onepharma_sync || "none", last: pilot.last_sync_at || "", notes: pilot.sync_notes || "" }); }, [stamp]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = form.sync !== (pilot.onepharma_sync || "none") || form.last !== (pilot.last_sync_at || "") || form.notes !== (pilot.sync_notes || "");
  const editable = cap.manageOperational && isRunning(pilot);
  const save = () => run(() => api.patch(url, { onepharma_sync: form.sync, last_sync_at: form.last || null, sync_notes: form.notes.trim() || null }),
    "Suivi OnePharma enregistré", "Impossible d'enregistrer le suivi OnePharma.");
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-sync-card">
      <SectionTitle hint="Déclaratif : saisi par l'équipe, non relié à OnePharma">Synchronisation OnePharma</SectionTitle>
      {editable ? (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="sync-state">État déclaré</Label>
            <Select value={form.sync} onValueChange={(v) => setForm({ ...form, sync: v })}>
              <SelectTrigger id="sync-state" data-testid="sync-state"><SelectValue /></SelectTrigger>
              <SelectContent>{SYNC_CHOICES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="sync-last">Dernière synchronisation constatée</Label><Input id="sync-last" type="date" value={form.last} onChange={(e) => setForm({ ...form, last: e.target.value })} data-testid="sync-last" /></div>
          <div className="space-y-1.5"><Label htmlFor="sync-notes">Notes</Label><Textarea id="sync-notes" rows={2} maxLength={2000} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="sync-notes" /></div>
          {pilot.sync_updated_at && <p className="text-2xs text-slate-400" data-testid="sync-author">Dernière modification le {fmtInstant(pilot.sync_updated_at)}{pilot.sync_author_name ? ` par ${pilot.sync_author_name}` : ""}</p>}
          {dirty && <div className="flex justify-end"><Button  size="sm" disabled={busy} onClick={save} data-testid="sync-save">{busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Enregistrer</Button></div>}
        </div>
      ) : (
        <div data-testid="sync-readonly">
          <Row label="État déclaré">{SYNC_LABELS[pilot.onepharma_sync] || SYNC_LABELS.none}</Row>
          <Row label="Dernière synchronisation constatée">{fmtDate(pilot.last_sync_at)}</Row>
          {pilot.sync_notes && <Row label="Notes">{pilot.sync_notes}</Row>}
          {pilot.sync_updated_at && <Row label="Mis à jour">le {fmtInstant(pilot.sync_updated_at)}{pilot.sync_author_name ? ` par ${pilot.sync_author_name}` : ""}</Row>}
        </div>
      )}
    </Card>
  );
}

function AutonomyCard({ pilot, cap, directory, user, onEvaluate }) {
  const a = pilot.autonomy || {};
  const by = a.evaluated_by === user?.id ? (user.name || user.email) : directory.find((u) => u.id === a.evaluated_by)?.name;
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-autonomy-card">
      <SectionTitle hint="Évaluation manuelle, datée">Autonomie</SectionTitle>
      <Row label="Niveau" testid="pilot-autonomy-level">{AUTONOMY_LABELS[a.level] || AUTONOMY_LABELS.not_evaluated}</Row>
      {a.evaluated_at && <Row label="Évaluée">le {fmtInstant(a.evaluated_at)}{by ? ` par ${by}` : ""}</Row>}
      {a.note && <Row label="Commentaire">{a.note}</Row>}
      {cap.manageOperational && pilot.status === "active" && <Button variant="outline" size="sm" className="mt-2" onClick={onEvaluate} data-testid="pilot-autonomy-button">Évaluer l'autonomie</Button>}
      {cap.manageOperational && pilot.status === "planned" && <p className="text-xs text-slate-400 mt-2">L'évaluation est possible une fois le pilote actif.</p>}
    </Card>
  );
}

// Explicit, per-pilot sharing of the numbers whose professional nature the system cannot know. Nothing is shared
// by default; a number changed on the fiche after sharing is masked by the backend and shows as "périmé" until shared again.
function SharingCard({ pilot, prospect, run, busy, url, dialog, setDialog }) {
  const rows = useMemo(() => buildShareRows(prospect, pilot), [prospect, pilot]);
  const effective = useMemo(() => rows.filter((r) => r.shared).map((r) => r.key), [rows]);
  const [selected, setSelected] = useState(effective);
  useEffect(() => { setSelected(effective); }, [effective]);
  const dirty = !sameSelection(selected, effective);
  const toggle = (key) => setSelected((s) => (s.includes(key) ? s.filter((k) => k !== key) : [...s, key]));
  const chosen = rows.filter((r) => selected.includes(r.key));
  const anySharing = rows.some((r) => r.shared || r.stale);
  const put = (fields, msg) => run(() => api.put(`${url}/shared-contact`, { fields }), msg, "Impossible de modifier le partage.");
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-sharing-card">
      <SectionTitle hint="Aucun numéro n'est partagé par défaut">Coordonnées partagées avec le responsable</SectionTitle>
      <p className="text-xs text-slate-500 mb-3">Le responsable du pilote voit toujours le nom, l'adresse, le téléphone principal et l'email de la pharmacie. Les numéros ci-dessous ne lui sont montrés que si vous les partagez.</p>
      <ul className="space-y-2">
        {rows.map((r) => (
          <li key={r.key} className="flex items-start gap-3" data-testid={`share-row-${r.key}`} data-shared={r.shared} data-stale={r.stale}>
            <Checkbox id={`share-${r.key}`} checked={selected.includes(r.key)} disabled={!r.hasValue || busy} onCheckedChange={() => toggle(r.key)} className="mt-0.5" data-testid={`share-check-${r.key}`} />
            <div className="min-w-0 flex-1">
              <label htmlFor={`share-${r.key}`} className="text-sm font-medium text-slate-900 cursor-pointer">{r.label}</label>
              <div className="text-sm text-slate-600">{r.hasValue ? r.value : "non renseigné sur la fiche"}</div>
            </div>
            {r.shared && <span className="text-2xs font-semibold rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 shrink-0" data-testid={`share-chip-${r.key}`}>Partagé</span>}
            {r.stale && <span className="text-2xs font-semibold rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 shrink-0" data-testid={`share-chip-${r.key}`} title="Le numéro a changé depuis le partage : il n'est plus transmis">Périmé, à repartager</span>}
          </li>
        ))}
      </ul>
      {rows.some((r) => r.stale) && <p className="mt-2 text-xs text-amber-800" data-testid="share-stale-note">Un numéro modifié après son partage n'est plus transmis au responsable, même si l'ancienne valeur est rétablie. Cochez-le de nouveau pour le repartager.</p>}
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        {anySharing && <Button variant="outline" size="sm" disabled={busy} onClick={() => setDialog("revoke")} data-testid="share-revoke-button">Révoquer tout le partage</Button>}
        {dirty && <Button  size="sm" disabled={busy} onClick={() => setDialog("share")} data-testid="share-save-button">Partager la sélection…</Button>}
      </div>
      <SharingDialog open={dialog === "share"} mode="share" rows={chosen} busy={busy} onClose={() => setDialog(null)} onSubmit={() => put(selected, selected.length ? "Partage enregistré" : "Partage révoqué")} />
      <SharingDialog open={dialog === "revoke"} mode="revoke" rows={[]} busy={busy} onClose={() => setDialog(null)} onSubmit={() => put([], "Partage révoqué")} />
    </Card>
  );
}

function ContactCard({ contact }) {
  if (!contact) return null;
  const oc = contact.operational_contact || {};
  const line = (label, value, href) => value && <Row label={label}>{href ? <a className="text-jamora hover:underline" href={href}>{value}</a> : value}</Row>;
  return (
    <Card className="p-4 md:p-5" data-testid="pilot-contact-card">
      <SectionTitle>Coordonnées de la pharmacie</SectionTitle>
      {line("Pharmacie", contact.pharmacy_name)}
      {line("Localisation", [contact.commune, contact.region].filter(Boolean).join(", "))}
      {line("Adresse", contact.address)}
      {line("Téléphone", contact.phone, contact.phone && `tel:${contact.phone}`)}
      {line("Email", contact.email, contact.email && `mailto:${contact.email}`)}
      {line("WhatsApp", contact.whatsapp, contact.whatsapp && `tel:${contact.whatsapp}`)}
      {line("Téléphone secondaire", contact.phone2, contact.phone2 && `tel:${contact.phone2}`)}
      {line("Contact opérationnel", [oc.name, oc.function].filter(Boolean).join(" · "))}
      {line("Téléphone du contact", oc.phone, oc.phone && `tel:${oc.phone}`)}
    </Card>
  );
}
